<template>
    <div class="employer-preferences" data-cy="profile-view-employer-preferences">
        <div class="left">
            <div>
                <span class="p-float-label" v-if="isViewing">
                    <span data-cy="profile-view-company-size">
                        <ul class="tags"
                            data-cy="profile-view-company-sizes"
                            v-if="editableCandidate.desiredCompanySizes?.length > 0">
                            <li v-for="size in editableCandidate.desiredCompanySizes"
                                :key="size.id">
                                {{t(size.name)}}
                            </li>
                        </ul>
                        <span v-else>
                            <i class="pi pi-times"/>
                            {{t("profile.multiselect.nothingselected")}}
                        </span>
                    </span>
                    <label>{{t("employer.preferences.desired.company.size")}}</label>
                </span>
                <span class="p-no-float-label" v-else>
                    <h5>{{t("employer.preferences.desired.company.size.title")}}</h5>
                    <ul data-cy="profile-edit-company-size-value"
                        class="tags"
                        v-if="editableCandidate.desiredCompanySizes?.length && editableCandidate.desiredCompanySizes.length>0">
                        <li v-for="size in editableCandidate.desiredCompanySizes"
                            :key="size.id">
                            {{t(size.name)}}
                            <Button icon="pi pi-times"
                                    class="p-button-rounded p-button-outlined"
                                    data-cy="wizard-chosen-company-size"
                                    @click="handleEmployerSizeRemoval($event, size)"/>
                        </li>
                    </ul>

                    <MultiSelect v-model="v$.desiredCompanySizes.$model"
                                 display="none"
                                 data-cy="wizard-choose-company-size"
                                 :class="{ 'p-invalid': v$.desiredCompanySizes.$error }"
                                 :options="props.availableCompanySizes"
                                 :filter="true"
                                 :autoFilterFocus="true"
                                 filterIcon="pi pi-search"
                                 :filterMatchMode="contains"
                                 optionLabel="name">
                        <template #option="slotProps">
                            {{slotProps.option.name}} {{t("staff.members")}}
                        </template>
                    </MultiSelect>
                    <label>{{t("employer.preferences.desired.company.size")}}</label>
                    <small data-cy="profile-edit-company-size-error"
                           class="p-error p-mt-0"
                           v-for="(error, index) of v$.desiredCompanySizes.$errors"
                           :key="index">
                        {{error.$message}}
                    </small>
                </span>

            </div>
            <div>
                <h5 v-if="!isViewing">
                    {{t("employer.preferences.choose.company.type")}}
                    <Tooltip :message="t('employer.preferences.choose.company.type.tooltip')" />
                </h5>
                <div v-else class="label">{{t("employer.preferences.employer.type")}}</div>
                <span class="p-no-float-label">
                    <span v-if="isViewing && editableCandidate.desiredEmployerTypes?.length === 0">
                        <i class="pi pi-times" />
                        {{t("profile.multiselect.nothingselected")}}
                    </span>
                    <ul data-cy="profile-edit-desiredemployertype"
                        class="tags"
                        v-if="editableCandidate.desiredEmployerTypes?.length && editableCandidate.desiredEmployerTypes.length > 0">
                        <li v-for="employerType in editableCandidate.desiredEmployerTypes" :key="employerType"
                            data-cy="profile-edit-desiredemployertype-chosen">
                            {{getDisplayFriendlyEmployerType(employerType)}}
                            <Button icon="pi pi-times" v-if="!isViewing"
                                    class="p-button-rounded p-button-outlined"
                                    data-cy="profile-edit-desiredemployertype-remove"
                                    @click="handleEmployerTypeRemoval($event, employerType)" />
                        </li>
                    </ul>
                    <MultiSelect :options="employerTypes"
                                 v-if="!isViewing"
                                 display="none"
                                 :class="{ 'p-invalid': v$.desiredEmployerTypes.$error }"
                                 v-model="v$.desiredEmployerTypes.$model"
                                 optionLabel="value"
                                 data-cy="profile-edit-desiredemployertype-list"
                                 optionValue="type" />
                    <label v-if="!isViewing">{{t("employer.preferences.choose.employer.type")}}</label>
                    <small class="p-error p-mt-0"
                           v-for="(error, index) of v$.desiredEmployerTypes.$errors"
                           :key="index">
                        {{error.$message}}
                    </small>
                </span>
            </div>
            <div
                v-if="editableCandidate.desiredEmployerTypes && editableCandidate.desiredEmployerTypes.includes('consulting') && editableCandidate.brandId === BrandId.BigFour">
                <h5>{{t("employer.preferences.choose.client.size")}}</h5>
                <span class="p-no-float-label">
                    <span v-if="isViewing">
                        <span data-cy="profile-view-client-size">
                            <ul class="tags"
                                v-if="editableCandidate?.desiredClientSizes?.length > 0">
                                <li v-for="size in editableCandidate.desiredClientSizes"
                                    :key="size.id">
                                    {{t(size.name)}}
                                </li>
                            </ul>
                            <span v-else>
                                <i class="pi pi-times"/>
                                {{t("profile.multiselect.nothingselected")}}
                            </span>
                        </span>
                    </span>
                    <div v-else>
                        <ul data-cy="profile-edit-client-size-value"
                            class="tags"
                            v-if="editableCandidate?.desiredClientSizes?.length > 0">
                            <li v-for="size in editableCandidate.desiredClientSizes"
                                :key="size.id">
                                {{t(size.name)}}
                                <Button icon="pi pi-times"
                                        class="p-button-rounded p-button-outlined"
                                        data-cy="wizard-chosen-client-size"
                                        @click="handleClientSizeRemoval($event, size)"/>
                            </li>
                        </ul>
                        <MultiSelect v-model="v$.desiredClientSizes.$model"
                                     data-cy="wizard-choose-client-size"
                                     display="none"
                                     :class="{ 'p-invalid': v$.desiredClientSizes.$invalid }"
                                     :options="availableClientSizesOptions"
                                     :filter="true"
                                     :autoFilterFocus="true"
                                     filterIcon="pi pi-search"
                                     :filterMatchMode="contains"
                                     optionLabel="name">
                        </MultiSelect>
                        <label>{{t("employer.preferences.choose.client.size.label")}}</label>
                    </div>

                    <small data-cy="profile-edit-client-size-error"
                           class="p-error"
                           v-for="(error, index) of v$.desiredClientSizes.$errors"
                           :key="index">
                        {{error.$message}}
                    </small>
                </span>
            </div>
        </div>
        <div class="right">
            <div>
                <h5 v-if="!isViewing">
                    {{t("employer.preferences.choose.blacklist.company")}}
                </h5>
                <span data-cy="profile-view-excluded-companies" class="p-float-label" v-if="isViewing">
                    <ul class="tags"
                        v-if="computedCompanies && computedCompanies.length > 0">
                        <li v-for="company in computedCompanies" :key="company.id">
                            {{company.name}}
                        </li>
                    </ul>
                    <span v-else>
                        <i class="pi pi-times"/>
                        {{t("profile.multiselect.nothingselected")}}
                    </span>
                    <label>{{t("employer.preferences.not.interested.companies")}}</label>
                </span>
                <div v-else>
                    <span data-cy="profile-edit-excluded-companies" class="p-float-label">
                        <ul class="tags"
                            v-if="computedCompanies.length && computedCompanies.length > 0">
                            <li v-for="company in computedCompanies" :key="company.id">
                                {{company.name}}
                                <Button icon="pi pi-times"
                                        class="p-button-rounded p-button-outlined"
                                        @click="handleCompanyExclusionRemoval($event, company)"/>
                            </li>
                        </ul>
                        <AutoComplete class="excluded-companies-autocomplete"
                                      v-model="computedCompanies"
                                      data-cy="wizard-excluded-companies"
                                      :multiple="true"
                                      :suggestions="filteredCompanies"
                                      @complete="handleFilterInput"
                                      field="name"
                                      :placeholder="t('employer.preferences.search.companies')">

                            <template #item="slotProps">
                                <div v-if="slotProps.item.id<0"
                                     class="new-company"> {{t("employer.preferences.missing.company")}} <div
                                    class="p-button button-secondary">{{t("employer.preferences.missing.company.create")}}</div> </div>
                                <div v-else>{{slotProps.item.name}}</div>

                            </template>

                        </AutoComplete>
                    </span>
                </div>
            </div>
            <div class="exclude-information p-mt-1">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd"
                          d="M16.2008 9.00005C16.2008 10.9096 15.4422 12.741 14.092 14.0912C12.7417 15.4415 10.9103 16.2 9.00078 16.2C7.09122 16.2 5.25987 15.4415 3.90961 14.0912C2.55935 12.741 1.80078 10.9096 1.80078 9.00005C1.80078 7.09049 2.55935 5.25914 3.90961 3.90888C5.25987 2.55862 7.09122 1.80005 9.00078 1.80005C10.9103 1.80005 12.7417 2.55862 14.092 3.90888C15.4422 5.25914 16.2008 7.09049 16.2008 9.00005V9.00005ZM9.90078 5.40005C9.90078 5.63874 9.80596 5.86766 9.63718 6.03644C9.46839 6.20523 9.23948 6.30005 9.00078 6.30005C8.76209 6.30005 8.53317 6.20523 8.36439 6.03644C8.1956 5.86766 8.10078 5.63874 8.10078 5.40005C8.10078 5.16135 8.1956 4.93244 8.36439 4.76365C8.53317 4.59487 8.76209 4.50005 9.00078 4.50005C9.23948 4.50005 9.46839 4.59487 9.63718 4.76365C9.80596 4.93244 9.90078 5.16135 9.90078 5.40005ZM8.10078 8.10005C7.86209 8.10005 7.63317 8.19487 7.46439 8.36365C7.2956 8.53244 7.20078 8.76135 7.20078 9.00005C7.20078 9.23874 7.2956 9.46766 7.46439 9.63645C7.63317 9.80523 7.86209 9.90005 8.10078 9.90005V12.6C8.10078 12.8387 8.1956 13.0677 8.36439 13.2364C8.53317 13.4052 8.76209 13.5 9.00078 13.5H9.90078C10.1395 13.5 10.3684 13.4052 10.5372 13.2364C10.706 13.0677 10.8008 12.8387 10.8008 12.6C10.8008 12.3614 10.706 12.1324 10.5372 11.9637C10.3684 11.7949 10.1395 11.7 9.90078 11.7V9.00005C9.90078 8.76135 9.80596 8.53244 9.63718 8.36365C9.46839 8.19487 9.23948 8.10005 9.00078 8.10005H8.10078Z"
                          fill="#0ED3C0"/>
                </svg>
                <p class="p-ml-1">{{t("employer.preferences.company.default.exclude")}}</p>
            </div>
        </div>

        <div class="call-to-action mobile-bottom-nav" v-if="showButtons">
            <Button data-cy="button-profile-prev"
                    class="button-secondary button-back p-pl-3"
                    @click="prev()">
                <i class="pi pi-angle-left"/>{{t("back")}}
            </Button>
            <Button data-cy="button-profile-next"
                    class="button-secondary button-next p-pr-3"
                    @click="next()">
                {{t("continue")}} <i class="pi pi-angle-right"/>
            </Button>
        </div>
    </div>
</template>

<script lang="ts" setup>
import {computed, defineEmits, defineProps, getCurrentInstance, onMounted, ref,} from "vue";
import MultiSelect from "primevue/multiselect";
import {CandidateProfile} from "@/models/CandidateProfile";
import {CompanySize} from "@/models/CompanySize";
import {ClientSize} from "@/models/ClientSize";
import useVuelidate from "@vuelidate/core";
import {Company} from "@/models/Company";
import {createProfileService} from "@/services/profileService";
import {useI18n} from "vue-i18n";
import {
    getFinanceEmployerTypes,
    getLegalEmployerTypes,
    getTechEmployerTypes,
    prettyEmployerType
} from "@/models/employerTypes";
import {BrandId} from "@/models/BrandIds";
import {createJobTitleService} from "@/services/jobTitleService";

const emit = defineEmits(["next", "previous"]);
const props = defineProps<{
    isViewing: boolean;
    candidate: CandidateProfile;
    availableCompanySizes: CompanySize[];
    availableClientSizes: ClientSize[];
    showButtons: boolean;
    disableValidation: boolean;
}>();

const app = getCurrentInstance();

const {t} = useI18n();
const availableClientSizesOptions = [...props.availableClientSizes];
availableClientSizesOptions.map(element => element.name = t(element.name));

const jobTitles = ref();
const loadJobTitles = async function () {
    let service = await createJobTitleService();
    let response = await service.getAll();
    let data = await response.data;
    if (JSON.stringify(data) !== JSON.stringify(jobTitles.value)) {
        jobTitles.value = data;
    }

  const containsRechtsanwalt = computed(() => {
    return editableCandidate.value.jobTitles?.find(e => e.id === jobTitles.value?.filter(x => x.name == t('Rechtsanwalt'))[0]?.id) != null;
  });

  const containsWirtschaftsjurist = computed(() => {
    return editableCandidate.value.jobTitles?.find(e => e.id === jobTitles.value?.filter(x => x.name == t('Wirtschaftsjurist'))[0]?.id) != null;
  });

  const containsRechtsanwaltsfachangestellte = computed(() => {
    return editableCandidate.value.jobTitles?.find(e => e.id === jobTitles.value?.filter(x => x.name == t('Rechtsanwaltsfachangestellter'))[0]?.id) != null;
  });

  const containsRechtsfachwirt = computed(() => {
    return editableCandidate.value.jobTitles?.find(e => e.id === jobTitles.value?.filter(x => x.name == t('Rechtsfachwirt'))[0]?.id) != null;
  });

  const containsPartnerassistenz = computed(() => {
    return editableCandidate.value.jobTitles?.find(e => e.id === jobTitles.value?.filter(x => x.name == t('Partnerassistenz'))[0]?.id) != null;
  });




  const isLegalCandidate = computed(() => containsRechtsanwalt.value || containsWirtschaftsjurist.value || containsRechtsanwaltsfachangestellte.value || containsRechtsfachwirt.value || containsPartnerassistenz.value);
  employerTypes.value = editableCandidate.value.brandId == BrandId.ElliotBrowne ? getTechEmployerTypes(t) : (isLegalCandidate.value ? getLegalEmployerTypes(t) : getFinanceEmployerTypes(t));

}

onMounted(() => {
    loadJobTitles();
});


const editableCandidate = ref<CandidateProfile>(props.candidate);

const employerTypes = ref();

const filteredCompanies = ref<Company[]>();
//const filteredInterestedCompanies = ref<Company[]>();

let lastSearchTerm = "";
//let lastSearchInterestedTerm = "";

const handleFilterInput = async (event) => {

    const term = event.query as string;
    lastSearchTerm = term;
    if (term.length < 2) {
        filteredCompanies.value = [];
        return;
    }

    await filterDebounced(term);
};

//const handleInterestedFilterInput = async (event) => {

//    const term = event.query as string;
//    lastSearchInterestedTerm = term;
//    if (term.length < 2) {
//        filteredCompanies.value = [];
//        return;
//    }

//    await filterInterestedDebounced(term);
//};

const debounce = (fn, delay) => {
    let timeoutID: null | number;
    return function () {
        if (timeoutID)
            clearTimeout(timeoutID);
        const args = arguments;
        timeoutID = setTimeout(() => {
            fn.apply(this, args);
        }, delay);
    };
};

const filterDebounced = debounce(async (term: string) => {
    const service = await createProfileService(app);

    const companies = await service.getFilteredCompanies(term);

    if (lastSearchTerm != term) {
        return;
    }

    filteredCompanies.value = companies
        ? companies.sort((a, b) => a.name.localeCompare(b.name))
        : ([] as Company[]);

    filteredCompanies.value = [{id: -1, name: term}, ...filteredCompanies.value];

}, 500);


//const filterInterestedDebounced = debounce(async (term: string) => {
//    const service = await createProfileService(app);

//    const companies = await service.getFilteredCompanies(term);

//    if (lastSearchInterestedTerm != term) {
//        return;
//    }

//    filteredInterestedCompanies.value = companies
//        ? companies.sort((a, b) => a.name.localeCompare(b.name))
//        : ([] as Company[]);

//    filteredInterestedCompanies.value = [{ id: -1, name: term }, ...filteredInterestedCompanies.value];

//}, 500);

const computedCompanies = computed({
    get: () => {
        return editableCandidate.value.excludedCompanies;

    },
    set: (value) => {
        editableCandidate.value.excludedCompanies = value;

    },
});

//const computedInterestedCompanies = computed({
//    get: () => {
//        return editableCandidate.value.preferredCompanies;
//    },
//    set: (value) => {

//        if (!value.at(-1)?.company) {
//            let company = { ...value.at(-1) }
//            const preferredCompany = ref({})
//            preferredCompany.value.name = company.name;
//            preferredCompany.value.company = company;
//            value[value.length - 1] = preferredCompany.value ;
//        }
//        if (editableCandidate.value.preferredCompanies.filter(e => e.company.id === value.at(-1)?.company.id).length > 0) {
//            return;
//        }
//        editableCandidate.value.preferredCompanies = value;
//    },
//});

const rules = {
    desiredCompanySizes: {},
    desiredClientSizes: {},
    desiredEmployerTypes: {}
};

const v$ = useVuelidate(rules, editableCandidate);

const getDisplayFriendlyEmployerType = (employerType: string) => {
    let isTech = editableCandidate.value.brandId == BrandId.ElliotBrowne;
    return prettyEmployerType(employerType, isTech, t);
};

const handleEmployerSizeRemoval = async (event, size: CompanySize) => {
    editableCandidate.value.desiredCompanySizes =
        editableCandidate.value.desiredCompanySizes.filter(
            (element) => element.id != size.id
        );
};

const handleClientSizeRemoval = async (event: any, size: ClientSize) => {
    editableCandidate.value.desiredClientSizes =
        editableCandidate.value.desiredClientSizes.filter(
            (clientSize) => clientSize.id != size.id
        );
};

const handleCompanyExclusionRemoval = async (event, company: Company) => {
    editableCandidate.value.excludedCompanies =
        editableCandidate.value.excludedCompanies.filter(
            (comp) => comp.name != company.name
        );
};
const handleEmployerTypeRemoval = async (event, employerType: string) => {
    editableCandidate.value.desiredEmployerTypes =
        editableCandidate.value.desiredEmployerTypes.filter(
            et => et != employerType
        );
};
//const handleInterestedCompanyRemoval = async (event, company: Company) => {
//    editableCandidate.value.preferredCompanies =
//        editableCandidate.value.preferredCompanies.filter(
//            (comp) => comp.name != company.name
//        );
//};

const validate = () => {
    v$.value.$touch();
    if (v$.value.$invalid) {
        document.getElementById(v$.value.$errors[0].$property)?.focus();
        document.getElementById(v$.value.$errors[0].$property)?.scrollIntoView();
        return v$.value.$error;
    }
};
const next = () => {
    if (!validate()) {
        emit("next", editableCandidate.value);
    }
};

const prev = () => {
    emit("previous", editableCandidate.value);
};
</script>

